import React, { useState, useRef, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Table,
    FormGroup,
    Label,
    Spinner,
    Alert,
    Card,
    CardBody,
    Row,
    Col,
    Badge,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import axios from 'axios';

interface AvoirLine {
    line_id: string;
    description: string;
    qty: number;
    subprice: number;
    price_ttc: number;
    total_ht: number;
    total_ttc: number;
    total_tva: number;
    fk_product: string;
    product_label: string;
    product_ref: string;
    product_type: number;
}

interface AvoirData {
    entity: number;
    facture: {
        ref: string;
        id: string;
        date: string;
        date_lim_reglement: string;
        total_ht: string;
        total_ttc: string;
    };
    filtre_type: number;
    lines: AvoirLine[];
    message: string;
    success: boolean;
    query?: string;
}

interface InvoiceListItem {
    id: number;
    ref: string;
    type: string;
    label: string;
}

interface SearchResponse {
    success: boolean;
    message: string;
    data: InvoiceListItem[];
    term: string;
    entity: number;
    entityname: string;
}

interface RetourAvoirModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: any;
}

const RetourAvoirModal: React.FC<RetourAvoirModalProps> = ({ isOpen, toggle, user }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<InvoiceListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [avoirData, setAvoirData] = useState<AvoirData | null>(null);
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceListItem | null>(null);
    const [quantites, setQuantites] = useState<{ [key: string]: number }>({});
    const [descriptions, setDescriptions] = useState<{ [key: string]: string }>({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchList = async (search: string) => {
        if (search.length < 3) {
            setSearchResults([]);
            setDropdownOpen(false);
            return;
        }

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debouncing
        searchTimeoutRef.current = setTimeout(async () => {
            setSearchLoading(true);
            setDropdownOpen(true);

            const data = {
                action: 'searchref',
                entity: user.entity,
                type: "facture",
                filters: { "type": 0 },
                term: search.trim()
            };

            try {
                const response : any = await axios.post<SearchResponse>(
                    'https://api.apapiecesauto.fr/apiV2/',
                    data,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        }
                    }
                );
                if (response.success) {
                    setSearchResults(response.data || []);
                    setError('');
                } else {
                    setSearchResults([]);
                    setError(response.data.message || 'Aucune facture trouvée');
                }
            } catch (error) {
                console.error('Error searching invoices:', error);
                setSearchResults([]);
                setError('Erreur lors de la recherche des factures');
            } finally {
                setSearchLoading(false);
            }
        }, 300); // 300ms debounce delay
    };

    const handleInvoiceSelect = async (invoice: InvoiceListItem) => {
        setSelectedInvoice(invoice);
        setSearchTerm(invoice.ref);
        setSearchResults([]);
        setDropdownOpen(false);
        setError('');
        await handleSearch(invoice.id);
    };

    const handleSearch = async (invoiceId: number) => {
        if (!invoiceId) {
            setError('Veuillez sélectionner une facture');
            return;
        }
        
        setLoading(true);
        setError('');
        setSuccess('');
        setAvoirData(null);
        setQuantites({});
        setDescriptions({});
        setPdfUrl('');

        try {
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'invoicedetails',
                    entity: user.entity,
                    id: invoiceId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                setAvoirData(response);

                // Initialize quantities and descriptions
                const initialQuantites: { [key: string]: number } = {};
                const initialDescriptions: { [key: string]: string } = {};
                response.lines.forEach((line: AvoirLine) => {
                    initialQuantites[line.line_id] = 0;
                    initialDescriptions[line.line_id] = '';
                });
                setQuantites(initialQuantites);
                setDescriptions(initialDescriptions);

            } else {
                setError(response.message || 'Aucune facture trouvée');
            }
        } catch (error) {
            console.error('Error searching invoice:', error);
            setError('Erreur lors de la recherche de la facture');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = () => {
        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
        }
    };

    const handleQuantiteChange = (lineId: string, value: string) => {
        const numValue = parseInt(value) || 0;
        const line = avoirData?.lines.find(l => l.line_id === lineId);

        if (line) {
            if (numValue >= 0 && numValue <= line.qty) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: numValue
                }));
            } else if (numValue > line.qty) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: line.qty
                }));
            } else if (numValue < 0) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: 0
                }));
            }
        }
    };

    const handleDescriptionChange = (lineId: string, value: string) => {
        setDescriptions(prev => ({
            ...prev,
            [lineId]: value
        }));
    };

    const handleInputFocus = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (quantites[lineId] === 0) {
            e.target.value = '';
        }
    };

    const handleInputBlur = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            setQuantites(prev => ({
                ...prev,
                [lineId]: 0
            }));
        }
    };

    const handleValidate = async () => {
        if (!avoirData) {
            setError('Aucune facture sélectionnée');
            return;
        }

        // Check if at least one quantity is > 0
        const hasValidQuantities = Object.values(quantites).some(qty => qty > 0);
        if (!hasValidQuantities) {
            setError('Veuillez saisir au moins une quantité à retourner');
            return;
        }

        setValidating(true);
        setError('');
        setSuccess('');
        
        try {
            const itemsToReturn = avoirData.lines
                .filter(line => quantites[line.line_id] > 0)
                .map(line => ({
                    line_id: line.line_id,
                    qt: quantites[line.line_id],
                    reason: descriptions[line.line_id] || 'Retour sans motif spécifié',
                    product_ref: line.product_ref,
                    product_label: line.product_label
                }));
            

            const data = {
                action: 'retourAvoir',
                entity: user.entity,
                invoice_ref: avoirData.facture.ref,
                invoice_id: avoirData.facture.id,
                lines: itemsToReturn,
            }
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                data ,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                const totalAmount = calculateTotal();
                setSuccess(`Avoir créé avec succès! Montant: ${totalAmount.toFixed(2)} €`);

                if (response.resultPDF) {
                    setPdfUrl(response.resultPDF);
                }

            } else {
                setError(response.message || 'Erreur lors de la création de l\'avoir');
            }
        } catch (error) {
            console.error('Error creating avoir:', error);
            setError('Erreur lors de la création de l\'avoir');
        } finally {
            setValidating(false);
        }
    };

    const calculateTotal = () => {
        if (!avoirData) return 0;

        return avoirData.lines.reduce((total, line) => {
            const lineQty = quantites[line.line_id] || 0;
            const unitPrice = line.price_ttc;
            return total + (lineQty * unitPrice);
        }, 0);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const resetModal = () => {
        setSearchTerm('');
        setSearchResults([]);
        setSelectedInvoice(null);
        setAvoirData(null);
        setQuantites({});
        setDescriptions({});
        setError('');
        setSuccess('');
        setPdfUrl('');
        setLoading(false);
        setSearchLoading(false);
        setValidating(false);
        setDropdownOpen(false);
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={resetModal} size="lg" centered>
            <ModalHeader toggle={resetModal}>
                <i className="ri-file-text-line me-2"></i>
                Création d'Avoir
            </ModalHeader>

            <ModalBody>
                {/* Search Section with Dropdown */}
                <FormGroup>
                    <Label for="searchAvoir" className="fw-bold mb-2">
                        Rechercher une Facture
                    </Label>
                    <div className="position-relative" ref={searchRef}>
                        <InputGroup>
                            <InputGroupText className="bg-light">
                                <i className="ri-search-line"></i>
                            </InputGroupText>
                            <Input
                                type="text"
                                id="searchAvoir"
                                placeholder="Entrez au moins 3 caractères..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    handleSearchList(e.target.value);
                                }}
                                disabled={loading || validating}
                                className="border-start-0"
                            />
                            {searchLoading && (
                                <InputGroupText className="bg-white border-start-0">
                                    <Spinner size="sm" />
                                </InputGroupText>
                            )}
                        </InputGroup>

                        {/* Search Results Dropdown */}
                        {dropdownOpen && searchResults.length > 0 && (
                            <div
                                className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                                style={{
                                    zIndex: 1000,
                                    maxHeight: '250px',
                                    overflowY: 'auto'
                                }}
                            >
                                <div className="p-1">
                                    {searchResults.map((invoice) => (
                                        <div
                                            key={invoice.id}
                                            className="p-2 border-bottom hover-pointer"
                                            onClick={() => handleInvoiceSelect(invoice)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong className="text-primary">{invoice.ref}</strong>
                                                    <div className="small text-muted">{invoice.label}</div>
                                                </div>
                                                <i className="ri-arrow-right-s-line text-muted"></i>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {dropdownOpen && searchTerm.length >= 3 && !searchLoading && searchResults.length === 0 && (
                            <div
                                className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center"
                                style={{ zIndex: 1000 }}
                            >
                                <i className="ri-search-line fs-4 text-muted mb-2 d-block"></i>
                                <div className="text-muted small">Aucune facture trouvée</div>
                            </div>
                        )}
                    </div>
                    {selectedInvoice && (
                        <div className="mt-2">
                            <Alert color="info" className="py-2 mb-0">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Facture sélectionnée :</strong> {selectedInvoice.ref}
                                    </div>
                                    <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedInvoice(null);
                                            setSearchTerm('');
                                            setAvoirData(null);
                                        }}
                                        className="p-0"
                                    >
                                        <i className="ri-close-line"></i>
                                    </Button>
                                </div>
                            </Alert>
                        </div>
                    )}
                </FormGroup>

                {/* Alerts */}
                {error && <Alert color="danger" className="py-2">{error}</Alert>}
                {success && <Alert color="success" className="py-2">{success}</Alert>}

                {/* Loading Spinner for Invoice Details */}
                {loading && (
                    <div className="text-center my-4">
                        <Spinner color="primary" />
                        <div className="mt-2 text-muted">Chargement des détails de la facture...</div>
                    </div>
                )}

                {/* Avoir Information */}
                {avoirData && !loading && (
                    <Card className="mt-3 border-info">
                        <CardBody>
                            {/* Invoice Header Info */}
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="d-flex align-items-center mb-2">
                                        <Badge color="info" className="me-2 fs-14">
                                            Facture
                                        </Badge>
                                        <strong className="fs-16">{avoirData.facture.ref}</strong>
                                    </div>
                                    <div className="text-muted small">
                                        <div>Date: {formatDate(avoirData.facture.date)}</div>
                                        <div>Échéance: {formatDate(avoirData.facture.date_lim_reglement)}</div>
                                        <div>Total HT: {parseFloat(avoirData.facture.total_ht).toFixed(2)} €</div>
                                        <div>Total TTC: {parseFloat(avoirData.facture.total_ttc).toFixed(2)} €</div>
                                    </div>
                                </Col>
                            </Row>

                            {/* PDF Download for return document */}
                            {pdfUrl && (
                                <Row className="mb-3">
                                    <Col>
                                        <Alert color="info" className="py-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>Avoir généré avec succès</span>
                                                <Button
                                                    color="outline-info"
                                                    size="sm"
                                                    onClick={handleDownloadPdf}
                                                >
                                                    <i className="ri-file-pdf-line me-1"></i>
                                                    Télécharger l'Avoir
                                                </Button>
                                            </div>
                                        </Alert>
                                    </Col>
                                </Row>
                            )}

                            {/* Invoice Lines Table */}
                            <div className="table-responsive">
                                <Table bordered size="sm" className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '30%' }}>Description</th>
                                            <th className="text-center">Référence</th>
                                            <th className="text-center">Prix Unitaire TTC</th>
                                            <th className="text-center">Qte Facturée</th>
                                            <th className="text-center">Qte à Retourner</th>
                                            <th style={{ width: '25%' }} className="text-center">Motif du Retour</th>
                                            <th className="text-center">Sous-total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {avoirData.lines.map((line) => {
                                            const lineQty = quantites[line.line_id] || 0;
                                            const lineTotal = lineQty * line.price_ttc;

                                            return (
                                                <tr key={line.line_id}>
                                                    <td>
                                                        <div className="fw-medium">{line.product_label}</div>
                                                        <small className="text-muted">{line.description}</small>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge color="success">{line.product_ref}</Badge>
                                                    </td>
                                                    <td className="text-center fw-bold text-success">
                                                        {line.price_ttc.toFixed(2)} €
                                                    </td>
                                                    <td className="text-center fw-bold">
                                                        {line.qty}
                                                    </td>
                                                    <td style={{ width: '100px' }}>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max={line.qty}
                                                            value={quantites[line.line_id] === 0 ? 0 : quantites[line.line_id]}
                                                            onChange={(e) => handleQuantiteChange(line.line_id, e.target.value)}
                                                            onFocus={(e) => handleInputFocus(line.line_id, e)}
                                                            onBlur={(e) => handleInputBlur(line.line_id, e)}
                                                            disabled={validating || !!pdfUrl}
                                                            className="text-center"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            placeholder="Motif du retour..."
                                                            value={descriptions[line.line_id] || ''}
                                                            onChange={(e) => handleDescriptionChange(line.line_id, e.target.value)}
                                                            disabled={validating || !!pdfUrl}
                                                            className="small"
                                                        />
                                                    </td>
                                                    <td className="text-center fw-bold text-primary">
                                                        {lineTotal.toFixed(2)} €
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Total Calculation */}
                            <div className="mt-3 p-3 bg-light rounded">
                                <Row className="text-center align-items-center">
                                    <Col md={4}>
                                        <div className="text-muted small">Montant total de l'avoir</div>
                                        <div className="h3 text-info fw-bold mb-0">
                                            {calculateTotal().toFixed(2)} €
                                        </div>
                                        <small className="text-muted">TTC</small>
                                    </Col>

                                    <Col md={4}>
                                    </Col>

                                    <Col md={4}>
                                        {avoirData && !pdfUrl && (
                                            <Button
                                                color="info"
                                                size="lg"
                                                onClick={handleValidate}
                                                disabled={validating || Object.values(quantites).every(qty => qty === 0)}
                                                className="w-100"
                                            >
                                                {validating ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-file-text-line me-2"></i>
                                                        Créer l'Avoir
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </ModalBody>
        </Modal>
    );
};

export default RetourAvoirModal;